import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';

const BackdropWrapper = styled.div`
  position: fixed;
  inset: 0;
  width: 100vw;
  height: 100vh;
  pointer-events: none;
  z-index: 0;
  overflow: hidden;
`;

const Canvas = styled.canvas`
  width: 100%;
  height: 100%;
  display: block;
`;

const CELL = 8;
const GREENS = [
  'rgba(79, 188, 97, 0.30)',
  'rgba(79, 188, 97, 0.18)',
  'rgba(101, 199, 117, 0.22)',
  'rgba(58, 154, 76, 0.26)',
];
const ACCENT = 'rgba(212, 222, 41, 0.30)';
const DENSITY = 1 / 5500;
const PARALLAX_FACTOR = 0.18;
const EDGE_ZONE = 0.22;
const TICK_MS = 120;

const DigitalBackdrop = () => {
  const canvasRef = useRef(null);
  const wrapperRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const wrapper = wrapperRef.current;
    if (!canvas || !wrapper) return;

    const ctx = canvas.getContext('2d');
    const reducedMotion = typeof window !== 'undefined' && window.matchMedia
      ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
      : false;

    let pixels = [];
    let rafId = null;
    let running = true;
    let dpr = Math.max(1, window.devicePixelRatio || 1);
    let cols = 0;
    let rows = 0;
    let viewW = 0;
    let viewH = 0;
    let lastTick = 0;
    let scrollY = window.scrollY || window.pageYOffset || 0;

    const pickEdgeCol = () => {
      const t = Math.pow(Math.random(), 1.4) * EDGE_ZONE;
      return Math.random() < 0.5
        ? Math.floor(t * cols)
        : Math.floor((1 - t) * cols);
    };

    const makePixel = () => {
      const isAccent = Math.random() < 0.05;
      const size = 1 + Math.floor(Math.random() * 3);
      return {
        col: pickEdgeCol(),
        row: Math.floor(Math.random() * rows),
        size,
        color: isAccent ? ACCENT : GREENS[Math.floor(Math.random() * GREENS.length)],
        life: Math.random() * 0.6,
        targetLife: 0.4 + Math.random() * 0.5,
        fadeDir: 1,
        fadeSpeed: 0.004 + Math.random() * 0.01,
      };
    };

    const seed = () => {
      const target = Math.max(20, Math.round(viewW * viewH * DENSITY));
      pixels = new Array(target).fill(0).map(() => makePixel());
    };

    const resize = () => {
      const rect = wrapper.getBoundingClientRect();
      viewW = Math.max(1, Math.floor(rect.width));
      viewH = Math.max(1, Math.floor(rect.height));
      dpr = Math.max(1, window.devicePixelRatio || 1);
      canvas.width = viewW * dpr;
      canvas.height = viewH * dpr;
      canvas.style.width = `${viewW}px`;
      canvas.style.height = `${viewH}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      cols = Math.ceil(viewW / CELL);
      rows = Math.ceil(viewH / CELL);
      seed();
    };

    const draw = () => {
      ctx.clearRect(0, 0, viewW, viewH);

      const offsetY = scrollY * PARALLAX_FACTOR;

      for (let i = 0; i < pixels.length; i++) {
        const p = pixels[i];
        if (!reducedMotion) {
          p.life += p.fadeDir * p.fadeSpeed;
          if (p.life <= 0) {
            pixels[i] = makePixel();
            continue;
          } else if (p.life >= p.targetLife) {
            p.life = p.targetLife;
            p.fadeDir = -1;
          }
        }
        const baseY = p.row * CELL;
        const shiftedY = baseY - offsetY;
        const wrappedY = ((shiftedY % viewH) + viewH) % viewH;
        ctx.globalAlpha = Math.max(0, Math.min(1, p.life));
        ctx.fillStyle = p.color;
        ctx.fillRect(p.col * CELL, wrappedY, p.size * CELL, p.size * CELL);
      }
      ctx.globalAlpha = 1;
    };

    const loop = (now) => {
      if (!running) return;
      if (now - lastTick > TICK_MS) {
        lastTick = now;
        draw();
      }
      if (!reducedMotion) {
        rafId = requestAnimationFrame(loop);
      }
    };

    let scrollRafScheduled = false;
    const onScroll = () => {
      if (scrollRafScheduled) return;
      scrollRafScheduled = true;
      requestAnimationFrame(() => {
        scrollRafScheduled = false;
        scrollY = window.scrollY || window.pageYOffset || 0;
        draw();
      });
    };

    const handleVisibility = () => {
      if (document.hidden) {
        running = false;
        if (rafId) cancelAnimationFrame(rafId);
      } else if (!reducedMotion) {
        running = true;
        rafId = requestAnimationFrame(loop);
      }
    };

    resize();
    draw();
    if (!reducedMotion) {
      rafId = requestAnimationFrame(loop);
    }

    window.addEventListener('resize', resize);
    window.addEventListener('scroll', onScroll, { passive: true });
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      running = false;
      if (rafId) cancelAnimationFrame(rafId);
      window.removeEventListener('resize', resize);
      window.removeEventListener('scroll', onScroll);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, []);

  return (
    <BackdropWrapper ref={wrapperRef} aria-hidden="true">
      <Canvas ref={canvasRef} />
    </BackdropWrapper>
  );
};

export default DigitalBackdrop;
